import { create } from "zustand";
// import { persist } from "zustand/middleware"; // Remove persist
import { dbHelpers } from "../lib/db";
import { RadioStation, Track } from "../types";

interface LocalRadioStore {
  radioStations: RadioStation[];
  initialized: boolean;
  initialize: () => Promise<void>;
  addRadioStation: (station: RadioStation) => Promise<void>;
  updateRadioStation: (id: string, updatedStation: Partial<RadioStation>) => Promise<void>;
  deleteRadioStation: (id: string) => Promise<void>;
  getRadioStation: (id: string) => RadioStation | undefined;
  addTrackToRadioStation: (stationId: string, track: Track) => Promise<void>;
  removeTrackFromRadioStation: (stationId: string, trackId: string) => Promise<void>;
}

export const useLocalRadioStore = create<LocalRadioStore>((set, get) => ({
  radioStations: [],
  initialized: false,

  initialize: async () => {
    if (get().initialized) return;

    try {
      const storedStations = await dbHelpers.getAllRadioStations();
      set({ radioStations: storedStations, initialized: true });
    } catch (error) {
      console.error("Error initializing radio stations:", error);
      set({ initialized: true });
    }
  },

  addRadioStation: async (station) => {
    await dbHelpers.addRadioStation(station);
    set((state) => ({ radioStations: [...state.radioStations, station] }));
  },

  updateRadioStation: async (id, updatedStation) => {
    const currentStation = get().getRadioStation(id);
    if (!currentStation) return;

    const updatedFullStation = { ...currentStation, ...updatedStation };
    await dbHelpers.addRadioStation(updatedFullStation);
    set((state) => ({
      radioStations: state.radioStations.map((station) => (station.id === id ? updatedFullStation : station)),
    }));
  },

  deleteRadioStation: async (id) => {
    await dbHelpers.deleteRadioStation(id);
    set((state) => ({
      radioStations: state.radioStations.filter((station) => station.id !== id),
    }));
  },

  getRadioStation: (id) => get().radioStations.find((station) => station.id === id),

  addTrackToRadioStation: async (stationId, track) => {
    const station = get().getRadioStation(stationId);
    if (!station) return;

    const updatedStation = {
      ...station,
      tracks: [...station.tracks, track],
      trackCount: station.trackCount + 1,
    };

    await dbHelpers.addRadioStation(updatedStation);
    set((state) => ({
      radioStations: state.radioStations.map((s) => (s.id === stationId ? updatedStation : s)),
    }));
  },

  removeTrackFromRadioStation: async (stationId, trackId) => {
    const station = get().getRadioStation(stationId);
    if (!station) return;

    const updatedStation = {
      ...station,
      tracks: station.tracks.filter((track) => track.id !== trackId),
      trackCount: station.trackCount - 1,
    };

    await dbHelpers.addRadioStation(updatedStation);
    set((state) => ({
      radioStations: state.radioStations.map((s) => (s.id === stationId ? updatedStation : s)),
    }));
  },
}));

// Initialize the store
useLocalRadioStore.getState().initialize();

// Subscribe to changes and persist to IndexedDB
useLocalRadioStore.subscribe((state) => {
  if (state.initialized) {
    state.radioStations.forEach((station) => {
      dbHelpers.addRadioStation(station).catch(console.error);
    });
  }
});
